/**
 * FindMine PDP Integration - "Complete the Look" Widget
 *
 * Loads FindMine's product recommendation widget on product detail pages.
 * Uses IntersectionObserver to defer loading unless an above-the-fold FindMine
 * link needs early match state.
 *
 * Implementation guide: https://findmine.notion.site/PDP-Widget-Implementation-Guide-18f008cf0c714960888a7c3c78c6f70c
 *
 * @requires window.theme.apps.findmine.pdp_application_id - FindMine PDP application ID
 * @requires #findmine-app-container - DOM element where FindMine widget will render
 * @listens product-overview:variant-change - Fires when user selects a different color/variant
 */
/* global theme, document, window, console, IntersectionObserver */
/* eslint camelcase: "off" */
import { EVENTS, subscribe } from '@utilities/events';

const FINDMINE_LINK_SELECTOR = '[data-findmine-pdp-link]';
const FINDMINE_MISSING_APP_ID_WARNING = "FindMine integration: FindMinePDPApplicationID is not set.";

const getFindMineAppId = (() => {
  let hasWarned = false;

  return () => {
    const appId = theme.apps?.findmine?.pdp_application_id;
    if (appId) return appId;

    if (!hasWarned) {
      console.warn(FINDMINE_MISSING_APP_ID_WARNING);
      hasWarned = true;
    }

    return null;
  };
})();

/**
 * Builds a FindMine.match() compatible object from product and variant data.
 *
 * @param {Object} product - Product data from product-overview component
 * @param {string|number} product.id - Product ID
 * @param {Object} variant - Product variant data
 * @param {string} variant.color - Color/variant identifier
 * @param {boolean} variant.available - Whether the variant is in stock
 * @returns {Object|null} FindMine match configuration object, or null if invalid
 */
const buildFindMineItem = (product, variant) => {
  if (!isVariantUsable(variant) || !isProductUsable(product)) return null;

  const appId = getFindMineAppId();
  if (!appId) return null;

  return {
    application: appId,
    product_id: product.id,
    product_color_id: variant.color,
    product_in_stock: variant.available,
    return_pdp_item: true,
  };
};

const isVariantUsable = (variant) => {
  return Boolean(variant) && typeof variant === "object" && !Array.isArray(variant) && Boolean(variant.color);
};

const isProductUsable = (product) => {
  return Boolean(product?.id);
};

const resolveVariantForFindMine = (variant, eventData = {}) => {
  const detailPayload = eventData?.detail;
  const eventVariantCandidates = [
    variant,
    eventData?.variant,
    eventData?.selected_variant,
    detailPayload?.variant,
    detailPayload?.selected_variant,
  ];

  for (const candidate of eventVariantCandidates) {
    if (isVariantUsable(candidate)) return candidate;
  }

  return null;
};

const getProductForMatch = (product, eventData, resolvedVariant) => {
  if (isProductUsable(product)) return product;

  const detailPayload = eventData?.detail;
  const productFromPayload =
    eventData?.productOverview?.getProduct?.() ??
    eventData?.product ??
    detailPayload?.product ??
    null;

  if (isProductUsable(productFromPayload)) return productFromPayload;
  if (resolvedVariant?.product_id) return { id: resolvedVariant.product_id };

  return null;
};

/**
 * Handles variant changes and triggers FindMine match.
 *
 * When a user selects a different color or variant, this function builds
 * the match configuration and sends it to FindMine's API to get updated recommendations.
 *
 * @param {Object} product - Product data
 * @param {Object} variant - Product variant data
 * @param {Object} eventData - Event payload for fallback variant resolution
 */
const handleVariantChange = (product, variant, eventData = {}) => {
  const resolvedVariant = resolveVariantForFindMine(variant, eventData);
  if (!isVariantUsable(resolvedVariant)) return;

  const resolvedProduct = getProductForMatch(product, eventData, resolvedVariant);
  if (!isProductUsable(resolvedProduct)) return;

  const findMineItem = buildFindMineItem(resolvedProduct, resolvedVariant);
  if (findMineItem) {

    try {
      window.FindMine?.match(findMineItem);
    } catch (error) {
      console.error("Error matching FindMine: ", error);
    }
  }
};

/**
 * Initializes FindMine integration
 *
 * Sets up event listener for variant changes and performs initial match
 * with the currently selected variant. Called after FindMine script loads.
 */
const initializeFindMine = () => {
  subscribe(EVENTS.PRODUCT_OVERVIEW_VARIANT_CHANGE, (data) => {
    const product =
      data?.productOverview?.getProduct?.() ??
      data?.productOverview?.product ??
      null;
    handleVariantChange(product, data?.variant, data);
  });

  const productOverview = document.querySelector("product-overview");
  if (productOverview) {
    handleVariantChange(
      productOverview.getProduct(),
      productOverview.getVariant(),
      { productOverview }
    );
  }
};

/**
 * Loads the FindMine external script
 *
 * Creates and injects the FindMine script tag into the page.
 * Once loaded, initializes the FindMine integration.
 */
const loadFindMineScript = () => {
  const appId = getFindMineAppId();
  if (!appId) return;

  const script = document.createElement("script");
  script.src = `//js.findmine.com/${appId}.module.js`;
  script.onload = () => {
    initializeFindMine();
  };
  script.onerror = () => {
    console.warn("FindMine integration: Failed to load FindMine script.");
  };
  document.head.appendChild(script);
};

/**
 * Sets up FindMine loading
 *
 * Loads the FindMine script immediately when link blocks need match state.
 * Otherwise, the script loads when the container is about to enter the viewport
 * (200px before).
 *
 * Once the script loads and initializes, the observer is disconnected as the
 * variant change listener handles all future interactions.
 */
const observeFindMineContainer = () => {
  const findmineContainer = document.querySelector('#findmine-app-container');
  if (!findmineContainer || !getFindMineAppId()) return;

  if (document.querySelector(FINDMINE_LINK_SELECTOR)) {
    loadFindMineScript();
    return;
  }

  // Create intersection observer with a small threshold
  const observer = new IntersectionObserver((entries) => {
    if (!entries[0]?.isIntersecting) return;

    loadFindMineScript();
    // Stop observing after first intersection
    observer.disconnect();
  }, {
    // Start loading when container is about to enter viewport (200px before)
    rootMargin: '200px',
    threshold: 0
  });

  // Start observing the FindMine container
  observer.observe(findmineContainer);
};

// Start observing for the FindMine container
observeFindMineContainer();
